import { DateRangeSelect, ModalFilters } from "@4uhub/lib4uhub";
import { Box, Stack, TextField } from "@mui/material";
import React, { Dispatch, SetStateAction, memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

export interface IFilterParams {
  CreatedStart: Date | null;
  CreatedEnd: Date | null;
  RequestIdentifier: string;
  AuthorizationIntegrationId: string;
  ProcedureIntegrationId: string;
  IndividualBeneficiaryId: string;
  IndividualBeneficiaryName: string;
  PatientEncounterNumber: string;
  PrescriptionNumber: string;
}

interface IMacroFilterProps {
  onFilterParams: Dispatch<SetStateAction<IFilterParams>>;
  filterParams: IFilterParams;
}

const translationPath = "page.4uBus.logs.patientEncounterExecuteRequest.";

const fieldWrapperSx = {
  flex: "1 1 100%",
  minWidth: 0,
};

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);
  const { t } = useTranslation();

  const [resetDateRange, setResetDateRange] = useState(false);

  const badgeCount = useMemo(() => {
    const initial = initialState.current;
    let count = 0;

    if (filterParams.IndividualBeneficiaryId !== initial.IndividualBeneficiaryId)
      count += 1;
    if (
      filterParams.IndividualBeneficiaryName !== initial.IndividualBeneficiaryName
    )
      count += 1;
    if (filterParams.PatientEncounterNumber !== initial.PatientEncounterNumber)
      count += 1;
    if (filterParams.PrescriptionNumber !== initial.PrescriptionNumber)
      count += 1;

    return count;
  }, [filterParams, initialState]);

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setResetDateRange((prev) => !prev);
  };

  return (
    <Stack spacing={0.75} sx={{ width: "100%" }}>
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
        <DateRangeSelect
          setStartDate={(date) => setFilter({ CreatedStart: date })}
          setEndDate={(date) => setFilter({ CreatedEnd: date })}
          setEnableFields={() => {}}
          resetToToday={resetDateRange}
        />

        <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
          <TextField
            value={filter.RequestIdentifier}
            size="small"
            label={t(translationPath + "request_identifier") || "Identificador da Requisição"}
            fullWidth
            onChange={(e) => setFilter({ RequestIdentifier: e.target.value })}
          />
        </Box>

        <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
          <TextField
            value={filter.AuthorizationIntegrationId}
            size="small"
            label={t(translationPath + "authorization_integration_id") || "Autorização"}
            fullWidth
            onChange={(e) =>
              setFilter({ AuthorizationIntegrationId: e.target.value })
            }
          />
        </Box>

        <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
          <TextField
            value={filter.ProcedureIntegrationId}
            size="small"
            label={t(translationPath + "procedure_integration_id") || "Procedimento"}
            fullWidth
            onChange={(e) => setFilter({ ProcedureIntegrationId: e.target.value })}
          />
        </Box>

        <FiltersActions
          onFilter={filterHandler}
          onClean={cleanHandler}
          showCleanBtn={showCleanBtn}
        />

        <ModalFilters
          badgeCount={badgeCount}
          actionsDisabled={false}
          onFilter={filterHandler}
          onCleanFilter={cleanHandler}
          triggerVariant="button"
          triggerLabel={t(translationPath + "additionalFilters") || "Mais filtros"}
          triggerButtonVariant="contained"
        >
          <Stack
            direction="row"
            gap={1.5}
            alignItems="flex-start"
            flexWrap="wrap"
            sx={{ width: "100%" }}
          >
            <Box sx={fieldWrapperSx}>
              <TextField
                value={filter.IndividualBeneficiaryId}
                size="small"
                label={
                  t(translationPath + "individual_beneficiary_id") ||
                  "ID do Beneficiário"
                }
                fullWidth
                onChange={(e) =>
                  setFilter({ IndividualBeneficiaryId: e.target.value })
                }
              />
            </Box>

            <Box sx={fieldWrapperSx}>
              <TextField
                value={filter.IndividualBeneficiaryName}
                size="small"
                label={
                  t(translationPath + "individual_beneficiary_name") ||
                  "Nome do Beneficiário"
                }
                fullWidth
                onChange={(e) =>
                  setFilter({ IndividualBeneficiaryName: e.target.value })
                }
              />
            </Box>

            <Box sx={{ flex: "1 1 calc(50% - 6px)", minWidth: 180 }}>
              <TextField
                value={filter.PatientEncounterNumber}
                size="small"
                label={
                  t(translationPath + "patient_encounter_number") ||
                  "Número do Atendimento"
                }
                fullWidth
                onChange={(e) =>
                  setFilter({ PatientEncounterNumber: e.target.value })
                }
              />
            </Box>

            <Box sx={{ flex: "1 1 calc(50% - 6px)", minWidth: 180 }}>
              <TextField
                value={filter.PrescriptionNumber}
                size="small"
                label={
                  t(translationPath + "prescription_number") ||
                  "Número da Prescrição"
                }
                fullWidth
                onChange={(e) => setFilter({ PrescriptionNumber: e.target.value })}
              />
            </Box>
          </Stack>
        </ModalFilters>
      </Stack>
    </Stack>
  );
};

export default memo(MacroFilter);
